import React, { useState } from 'react';
import { motion } from 'framer-motion';
import projectsData from '../data/projectsData.json';
import Modal from '../components/02_Modal.js';

const ProjectsRetroDual = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const projects = projectsData;

  const colorOptions = [
    { bg: 'bg-cyan-400', border: 'border-cyan-600 dark:border-cyan-400' },
    { bg: 'bg-pink-400', border: 'border-pink-600 dark:border-pink-400' },
    { bg: 'bg-yellow-400', border: 'border-yellow-600 dark:border-yellow-400' },
    { bg: 'bg-green-400', border: 'border-green-600 dark:border-green-400' },
    { bg: 'bg-purple-400', border: 'border-purple-600 dark:border-purple-400' },
    { bg: 'bg-orange-400', border: 'border-orange-600 dark:border-orange-400' },
    { bg: 'bg-blue-400', border: 'border-blue-600 dark:border-blue-400' },
    { bg: 'bg-red-400', border: 'border-red-600 dark:border-red-400' },
  ];

  const openModal = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedProject(null), 300);
  };

  return (
    <div className="min-h-screen bg-[#F5E6D3] dark:bg-[#1a1a2e] relative overflow-hidden transition-colors duration-500">
      {/* Dot Grid Background - Light */}
      <div 
        className="absolute inset-0 opacity-20 dark:opacity-0 transition-opacity duration-500"
        style={{
          backgroundImage: `radial-gradient(circle, #8B7355 1px, transparent 1px)`,
          backgroundSize: '24px 24px'
        }}
      />
      
      {/* Dot Grid Background - Dark (Glowing) */}
      <div 
        className="absolute inset-0 opacity-0 dark:opacity-30 transition-opacity duration-500"
        style={{
          backgroundImage: `radial-gradient(circle, #00ff88 1px, transparent 1px)`,
          backgroundSize: '24px 24px'
        }}
      />

      {/* Scanline Effect - Dark Mode */}
      <div 
        className="absolute inset-0 opacity-0 dark:opacity-5 pointer-events-none transition-opacity duration-500"
        style={{
          backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 2px, #00ff88 2px, #00ff88 4px)`
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 py-12">
        
        {/* Page Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-block bg-white dark:bg-[#2d2d44] border-4 border-black dark:border-pink-400 px-8 py-4 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_20px_rgba(255,0,255,0.5)] mb-4 transition-all duration-500">
            <h1 className="text-5xl font-black uppercase tracking-tight transition-colors duration-500">
              MY <span className="text-purple-600 dark:text-pink-400 dark:drop-shadow-[0_0_20px_rgba(255,0,255,0.8)]">PROJECTS</span>
            </h1>
          </div>
          <p className="text-xl font-bold text-gray-800 dark:text-green-400 max-w-2xl mx-auto transition-colors duration-500">
            🔧 Engineering Solutions & Creative Builds
          </p>
        </motion.div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {projects.map((project, index) => {
            const colors = colorOptions[index % colorOptions.length];
            return (
              <motion.div
                key={project.id}
                className={`bg-[#FFF8E7] dark:bg-[#2d2d44] border-4 ${colors.border} shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] dark:shadow-[6px_6px_0px_0px_currentColor] hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] dark:hover:shadow-[8px_8px_0px_0px_currentColor] transition-all hover:translate-x-[-2px] hover:translate-y-[-2px] cursor-pointer group duration-500`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                onClick={() => openModal(project)}
              >
                {/* Window Title Bar */}
                <div className={`${colors.bg} border-b-4 ${colors.border} px-4 py-3 flex items-center justify-between transition-all duration-500`}>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-black dark:shadow-[0_0_10px_currentColor] transition-all duration-500"></div>
                    <span className="font-black uppercase tracking-tight text-sm text-black">
                      PROJECT_{String(project.id).padStart(2, '0')}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <div className="w-5 h-5 bg-white dark:bg-black border-2 border-black dark:border-white flex items-center justify-center transition-all duration-500">
                      <span className="text-xs font-black dark:text-white">?</span>
                    </div>
                  </div>
                </div>

                {/* Project Image */}
                <div className="relative overflow-hidden h-48">
                  <img 
                    src={project.images?.[0]?.src || '/api/placeholder/400/300'}
                    alt={project.images?.[0]?.alt || project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black dark:bg-cyan-400 bg-opacity-0 dark:bg-opacity-0 group-hover:bg-opacity-80 dark:group-hover:bg-opacity-90 transition-all duration-300 flex items-center justify-center">
                    <span className="text-white dark:text-black font-black text-xl uppercase opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      CLICK TO VIEW
                    </span>
                  </div>
                </div>

                {/* Project Info */}
                <div className="p-4">
                  <h3 className="text-xl font-black uppercase mb-2 leading-tight dark:text-white transition-colors duration-500">
                    {project.title}
                  </h3>
                  <p className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-4 transition-colors duration-500">
                    {project.shortDescription}
                  </p>
                  <div className="flex items-center justify-between">
                    <button className="bg-blue-400 dark:bg-yellow-400 hover:bg-blue-500 dark:hover:bg-yellow-500 border-3 border-black px-4 py-2 font-black uppercase text-sm transition-all inline-flex items-center gap-2 text-black duration-500">
                      LEARN MORE <span className="text-lg">→</span>
                    </button>
                    <div className={`w-8 h-8 bg-green-400 border-2 border-black dark:border-green-400 flex items-center justify-center font-black dark:shadow-[0_0_10px_rgba(0,255,0,0.6)] transition-all duration-500`}>
                      ✓
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Stats Window */}
        <motion.div
          className="bg-[#FFF8E7] dark:bg-[#2d2d44] border-4 border-black dark:border-pink-400 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_20px_rgba(255,0,255,0.5)] transition-all duration-500"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          {/* Window Title Bar */}
          <div className="bg-gradient-to-r from-green-400 to-emerald-500 dark:from-pink-400 dark:to-purple-500 border-b-4 border-black dark:border-pink-400 px-4 py-3 flex items-center justify-between transition-all duration-500">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-black dark:bg-pink-400 dark:shadow-[0_0_10px_rgba(255,0,255,0.8)]"></div>
              <span className="font-black text-lg uppercase tracking-tight text-black dark:text-pink-300 transition-colors duration-500">📊 PROJECT_STATS.exe</span>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center bg-white dark:bg-[#1a1a2e] border-4 border-black dark:border-cyan-400 p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_15px_rgba(0,255,255,0.5)] transition-all duration-500">
                <div className="text-5xl font-black text-purple-600 dark:text-cyan-400 mb-2 dark:drop-shadow-[0_0_15px_rgba(0,255,255,0.8)] transition-all duration-500">
                  {projects.length}
                </div>
                <div className="font-black uppercase text-xs tracking-tight dark:text-white transition-colors duration-500">
                  Total Projects
                </div>
              </div>
              <div className="text-center bg-white dark:bg-[#1a1a2e] border-4 border-black dark:border-pink-400 p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_15px_rgba(255,0,255,0.5)] transition-all duration-500">
                <div className="text-5xl font-black text-blue-600 dark:text-pink-400 mb-2 dark:drop-shadow-[0_0_15px_rgba(255,0,255,0.8)] transition-all duration-500">
                  10+
                </div>
                <div className="font-black uppercase text-xs tracking-tight dark:text-white transition-colors duration-500">
                  Technologies Used
                </div>
              </div>
              <div className="text-center bg-white dark:bg-[#1a1a2e] border-4 border-black dark:border-green-400 p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_15px_rgba(0,255,0,0.5)] transition-all duration-500">
                <div className="text-5xl font-black text-green-600 dark:text-green-400 mb-2 dark:drop-shadow-[0_0_15px_rgba(0,255,0,0.8)] transition-all duration-500">
                  3
                </div>
                <div className="font-black uppercase text-xs tracking-tight dark:text-white transition-colors duration-500">
                  Areas of Focus
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-20 right-10 w-16 h-16 bg-purple-400 border-4 border-purple-600 dark:border-purple-400 rotate-12 hidden lg:block dark:shadow-[0_0_30px_rgba(138,43,226,0.6)] dark:animate-pulse transition-all duration-500"></div>
      <div className="absolute bottom-20 left-10 w-20 h-20 bg-yellow-400 border-4 border-yellow-600 dark:border-yellow-400 rounded-full hidden lg:block dark:shadow-[0_0_30px_rgba(255,255,0,0.6)] dark:animate-pulse transition-all duration-500"></div>
      <div className="absolute top-1/2 right-20 w-12 h-12 bg-cyan-400 border-4 border-cyan-600 dark:border-cyan-400 -rotate-12 hidden lg:block dark:shadow-[0_0_30px_rgba(0,255,255,0.6)] dark:animate-pulse transition-all duration-500"></div>

      {/* Modal */}
      <Modal 
        isOpen={isModalOpen}
        onClose={closeModal}
        title={selectedProject?.title}
        description={selectedProject?.longDescription}
        images={
          selectedProject?.images?.length > 1
            ? selectedProject.images.slice(1).map((img) => ({
                src: img.src,
                alt: img.alt || `Image for ${selectedProject.title}`,
                caption: img.caption,
              }))
            : []
        }
      />
    </div>
  );
};

export default ProjectsRetroDual;